/**
 * Pure helpers for the home page's city cards (components/ExploreByCity.tsx),
 * kept out of the component file so they can be unit-tested and so the
 * component module only exports components (fast refresh).
 */
import type { ResolvedSaudiCity } from '../../config/saudiCities';
import type { SearchRequest } from '../../services/ragSearchService';

/**
 * The count query for a city card. The exact database id when one is known;
 * otherwise the English name, which the backend's fuzzy `city` match
 * normalizes best. The card's link carries the SAME filter, so the number on
 * the card is the number of results the visitor lands on.
 */
export function cityCountRequest(city: ResolvedSaudiCity): SearchRequest {
  return city.cityId ? { cityId: city.cityId } : { city: city.nameEn };
}

/**
 * /search deep link for a city card: every listing type and category in that
 * city (`all` sentinels, because /search defaults to rent + residential).
 * With an id, `city` is only the label for the results page's city chip, in
 * the visitor's language (the same contract as utils/heroSearchParams).
 */
export function cityCardHref(city: ResolvedSaudiCity, isArabic: boolean): string {
  const params = new URLSearchParams();
  if (city.cityId) {
    params.set('cityId', city.cityId);
    params.set('city', isArabic ? city.nameAr : city.nameEn);
  } else {
    params.set('city', city.nameEn);
  }
  params.set('listingType', 'all');
  params.set('category', 'all');
  return `/search?${params.toString()}`;
}

/**
 * Grid spans that never leave a hole in a row:
 *   phones   2 columns; an odd last card spans the full row
 *   tablets  a 6-column track: rows of three (span 2 each); a last row of two
 *            spans 3 each, a last single card spans all 6
 *   desktop  one row (`lg:grid-cols-5` for the five featured cities)
 * The class names are literal so Tailwind's scanner keeps them.
 */
export function cityCardSpan(index: number, total: number): string {
  const classes: string[] = [];
  if (total % 2 === 1 && index === total - 1) classes.push('col-span-2');
  const lastRow = total % 3;
  const inLastRow = lastRow !== 0 && index >= total - lastRow;
  if (!inLastRow) classes.push('sm:col-span-2');
  else classes.push(lastRow === 2 ? 'sm:col-span-3' : 'sm:col-span-6');
  classes.push('lg:col-span-1');
  return classes.join(' ');
}
